import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import DocumentPackageCard from '../components/DocumentPackageCard';
import DocumentPageOverlay from '../components/DocumentPageOverlay';
import PublicPackageDetails from './PublicPackageDetails';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageContainerSx,
} from '../constants/documentTheme';
import { useSyncClientBranding } from '../contexts/ClientBrandingContext';
import { setPartnerCatalogToken, clearPartnerCatalogToken } from '../utils/partnerCatalogStorage';
import AdvisorInquiryForm from '../components/AdvisorInquiryForm';
import { getPackageCoverSrc } from '../utils/partnerListUtils';
import { partnerOxbloodBtnSx } from '../components/PartnerListChrome';

const PartnerCatalog = () => {
  const { catalogId } = useParams();
  const { t } = useTranslation();
  const [partner, setPartner] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [previewPackage, setPreviewPackage] = useState(null);
  const [advisorAskOpen, setAdvisorAskOpen] = useState(false);

  useSyncClientBranding(partner?.branding);

  const loadCatalog = useCallback(async () => {
    if (!catalogId) return;
    try {
      setLoading(true);
      setNotFound(false);
      const response = await api.get(`/travel-packages/catalog/${catalogId}?limit=48`);
      setPartner(response.data.partner || null);
      setPackages(response.data.packages || []);
      if (catalogId) {
        setPartnerCatalogToken(catalogId);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
        clearPartnerCatalogToken();
      }
      setPartner(null);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [catalogId]);

  useEffect(() => {
    if (!catalogId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    loadCatalog();
  }, [loadCatalog, catalogId]);

  const pageTitle = partner?.companyName
    ? t('partnerCatalog.titleWithPartner', { partner: partner.companyName })
    : t('partnerCatalog.title');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DOCUMENT.canvas, color: DOCUMENT.ink }}>
      <SEOHead
        title={pageTitle}
        description={t('partnerCatalog.description')}
        noIndex
      />

      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
          </Box>
        ) : notFound ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: DOCUMENT.display,
                fontSize: { xs: 28, md: 36 },
                mb: 2,
              }}
            >
              {t('partnerCatalog.notFoundTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink3, maxWidth: 480, mx: 'auto' }}>
              {t('partnerCatalog.notFoundBody')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ pt: { xs: 3, md: 4 }, pb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {partner?.logo ? (
                  <Box
                    component="img"
                    src={partner.logo}
                    alt={partner.companyName || ''}
                    sx={{ width: 56, height: 56, objectFit: 'contain' }}
                  />
                ) : null}
                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: DOCUMENT.display,
                      fontSize: { xs: 28, md: 40 },
                      lineHeight: 1.1,
                    }}
                  >
                    {pageTitle}
                  </Typography>
                  {partner?.description ? (
                    <Typography sx={{ mt: 1, color: DOCUMENT.ink3, maxWidth: 720 }}>
                      {partner.description}
                    </Typography>
                  ) : (
                    <Typography sx={{ mt: 1, color: DOCUMENT.ink3 }}>
                      {t('partnerCatalog.subtitle')}
                    </Typography>
                  )}
                  {partner?.id ? (
                    <Button
                      data-testid="catalog-ask-advisor"
                      onClick={() => setAdvisorAskOpen(true)}
                      sx={{ ...partnerOxbloodBtnSx, mt: 2 }}
                    >
                      {t('publicPackage.askThisAdvisor')}
                    </Button>
                  ) : null}
                </Box>
              </Box>
            </Box>

            {packages.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: DOCUMENT.ink2, mb: 1 }}>
                  {t('partnerCatalog.emptyTitle')}
                </Typography>
                <Typography sx={{ color: DOCUMENT.ink3 }}>
                  {t('partnerCatalog.emptyBody')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2.75}>
                {packages.map((pkg) => {
                  const imageUrl = getPackageCoverSrc(pkg);
                  const durationDays = pkg.duration_days || 0;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                      <DocumentPackageCard
                        testId={`catalog-package-card-${pkg.id}`}
                        imageUrl={imageUrl}
                        imageAlt={pkg.title}
                        containImage={Boolean(pkg.cover_image)}
                        badges={[pkg.package_type].filter(Boolean)}
                        coverLabel={t('packagesPage.durationDays', { count: durationDays })}
                        eyebrow={pkg.package_type}
                        title={pkg.title}
                        location={pkg.destination}
                        description={pkg.short_description}
                        rows={[
                          { label: t('packagesPage.destination'), value: pkg.destination },
                          {
                            label: t('packagesPage.duration'),
                            value: t('packagesPage.daysCount', { count: durationDays }),
                          },
                          {
                            label: t('packagesPage.price'),
                            value: `$${Number(pkg.base_price || 0).toLocaleString()}`,
                            extra: t('packagesPage.perPerson'),
                          },
                        ]}
                        actionLabel={t('packagesPage.openPackage')}
                        onClick={() => pkg.slug && setPreviewPackage({ slug: pkg.slug, title: pkg.title })}
                        onAction={() => pkg.slug && setPreviewPackage({ slug: pkg.slug, title: pkg.title })}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </Container>

      <DocumentPageOverlay
        open={Boolean(previewPackage?.slug)}
        onClose={() => setPreviewPackage(null)}
        title={previewPackage?.title || t('packagesPage.openPackage')}
        testId="catalog-package-preview-modal"
      >
        {previewPackage?.slug ? (
          <PublicPackageDetails
            previewSlug={previewPackage.slug}
            catalogToken={catalogId}
          />
        ) : null}
      </DocumentPageOverlay>
      <AdvisorInquiryForm
        open={advisorAskOpen}
        onClose={() => setAdvisorAskOpen(false)}
        mode="general"
        partnerId={partner?.id}
        partnerName={partner?.companyName}
        catalogToken={catalogId}
      />
    </Box>
  );
};

export default PartnerCatalog;
